import { InboundProcess } from '@features/supplyChain/types';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { SortableHeader } from '../../../tailwindComponents/SortableHeader';
import { InboundSortField, SortDirection } from '../types';
import { CollapsibleInboundRow } from './CollapsibleInboundRow';

interface InboundShipmentsTableProps {
  inboundProcesses: InboundProcess[];
  sortField: InboundSortField | null;
  sortDirection: SortDirection;
  onSort: (field: InboundSortField) => void;
  selectedIds: Set<string>;
  onSelectChange: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onOpenProcess: (inboundProcess: InboundProcess) => void;
}

export function InboundShipmentsTable({
  inboundProcesses,
  sortField,
  sortDirection,
  onSort,
  selectedIds,
  onSelectChange,
  onSelectAll,
  onOpenProcess,
}: InboundShipmentsTableProps) {
  const allSelected =
    inboundProcesses.length > 0 &&
    inboundProcesses.every((p) => selectedIds.has(p.id));

  return (
    <div>
      {/* Header */}
      <div className='flex items-center gap-5 bg-gray-100 p-5'>
        <ChevronRightIcon className='h-6 w-6 shrink-0 text-gray-400' />
        <SortableHeader<InboundSortField>
          label='Order Pool'
          field='orderPool'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
          className='max-w-[160px] min-w-[120px] justify-center'
        />
        <SortableHeader<InboundSortField>
          label='Note ID'
          field='noteId'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <SortableHeader<InboundSortField>
          label='From'
          field='from'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <SortableHeader<InboundSortField>
          label='To'
          field='to'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <SortableHeader<InboundSortField>
          label='SKUs'
          field='skus'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
          className='max-w-[150px] min-w-[74px]'
        />
        <SortableHeader<InboundSortField>
          label='Pallets'
          field='pallets'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
          className='max-w-[150px] min-w-[74px]'
        />
        {/* Checkbox select all */}
        <div className='shrink-0'>
          <input
            type='checkbox'
            checked={allSelected}
            onChange={(e) => onSelectAll(e.target.checked)}
            className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
          />
        </div>
      </div>

      {/* Rows */}
      <div className='flex flex-col'>
        {inboundProcesses.length === 0 ? (
          <div className='border-t border-gray-200 px-5 py-8 text-center text-sm text-gray-500'>
            No inbound shipments found
          </div>
        ) : (
          inboundProcesses.map((inboundProcess) => (
            <CollapsibleInboundRow
              key={inboundProcess.id}
              inboundProcess={inboundProcess}
              selected={selectedIds.has(inboundProcess.id)}
              onSelectChange={onSelectChange}
              onOpenProcess={onOpenProcess}
            />
          ))
        )}
      </div>
    </div>
  );
}
